/**
 * Privacy-safe seller intake analytics (Sell Your Car M9).
 * Never sends seller PII, notes, raw payloads, submission IDs, or guardrail internals.
 */
import { trackEvent } from "../../analytics/analytics";
import { isPublicAnalyticsEnabled } from "../../analytics/analyticsConfig";
import {
  SELLER_INTAKE_ANALYTICS_ALLOWED_KEYS,
  SELLER_INTAKE_ANALYTICS_EVENTS,
  SELLER_SUBMISSION_STATUS_EVENT_MAP,
} from "../../constants/sellerIntakeAnalyticsConstants";
import { SELL_YOUR_CAR_PATH } from "../../constants/sellYourCarPageConstants";
import { SELLER_INTAKE_ANALYTICS_FORBIDDEN_FIELDS as SELLER_ANALYTICS_BLOCKED_KEYS } from "./sellerIntakePrivacy";
import { buildSellerSubmissionAttribution } from "./sellerIntakeAttribution";
import {
  SELLER_SOURCE_COMPONENTS,
  SELLER_SOURCE_PAGES,
  SELLER_SUBMISSION_STATUSES,
} from "./sellerIntakeConstants";

export const SELLER_INTAKE_ANALYTICS_DOMAIN = "automap_seller_intake";

const ALLOWED_KEY_SET = new Set(SELLER_INTAKE_ANALYTICS_ALLOWED_KEYS);
const BLOCKED_KEY_SET = new Set(SELLER_ANALYTICS_BLOCKED_KEYS);

/** Fields mapped to form steps for progress analytics. */
const FIELD_FORM_STEPS = Object.freeze({
  sellerType: "contact",
  sellerName: "contact",
  sellerPhone: "contact",
  sellerEmail: "contact",
  preferredContactMethod: "contact",
  city: "contact",
  department: "contact",
  vehicleMake: "vehicle",
  vehicleModel: "vehicle",
  vehicleYear: "vehicle",
  vehicleTrim: "vehicle",
  vehicleMileageKm: "vehicle",
  ownershipStatus: "vehicle",
  askingPrice: "pricing",
  currency: "pricing",
  conditionSummary: "condition",
  notes: "condition",
  hasPhotos: "condition",
  consentAccepted: "consent",
  marketingOptIn: "consent",
});

const FORM_STEP_ORDER = ["contact", "vehicle", "pricing", "condition", "consent"];

function pickString(value) {
  if (value == null) return "";
  return String(value).trim();
}

/**
 * Normalized city label for analytics (no addresses/PII).
 * @param {string|null|undefined} city
 * @returns {string|null}
 */
export function normalizeSellerCityForAnalytics(city) {
  const text = pickString(city)
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!text || text.length < 2) return null;
  const digitsOnly = text.replace(/\D/g, "");
  if (digitsOnly.length >= 7 && digitsOnly.length / text.replace(/\s/g, "").length > 0.6) {
    return null;
  }
  return text.length > 80 ? text.slice(0, 80) : text;
}

function containsBlockedKey(key) {
  return BLOCKED_KEY_SET.has(key);
}

/**
 * @returns {boolean}
 */
export function isSellerIntakeAnalyticsEnabled() {
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "test") {
    return process.env.REACT_APP_SELLER_INTAKE_ANALYTICS_IN_TESTS === "true";
  }
  return isPublicAnalyticsEnabled();
}

/**
 * @returns {'mobile'|'tablet'|'desktop'|null}
 */
export function getSellerIntakeViewportCategory() {
  if (typeof window === "undefined") return null;
  const width = window.innerWidth;
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

/**
 * @param {number|null|undefined} year
 * @returns {string}
 */
export function getVehicleYearBucket(year) {
  const value = Number(year);
  if (!Number.isFinite(value) || value <= 0) return "unknown";
  if (value < 2000) return "before_2000";
  if (value <= 2009) return "2000_2009";
  if (value <= 2014) return "2010_2014";
  if (value <= 2019) return "2015_2019";
  if (value <= 2024) return "2020_2024";
  return "2025_plus";
}

/**
 * @param {number|null|undefined} km
 * @returns {string}
 */
export function getMileageBucket(km) {
  const value = Number(km);
  if (!Number.isFinite(value) || value < 0) return "unknown";
  if (value <= 20000) return "0_20000";
  if (value <= 50000) return "20001_50000";
  if (value <= 100000) return "50001_100000";
  if (value <= 150000) return "100001_150000";
  if (value <= 200000) return "150001_200000";
  return "200001_plus";
}

/**
 * @param {number|null|undefined} price
 * @param {string|null|undefined} currency
 * @returns {string}
 */
export function getAskingPriceBucket(price, currency = "COP") {
  const value = Number(price);
  if (!Number.isFinite(value) || value <= 0) return "unknown";
  const normalizedCurrency = pickString(currency).toUpperCase() || "COP";
  if (normalizedCurrency !== "COP") return "unknown";

  if (value < 20_000_000) return "under_20m";
  if (value < 40_000_000) return "20m_40m";
  if (value < 70_000_000) return "40m_70m";
  if (value < 100_000_000) return "70m_100m";
  if (value < 150_000_000) return "100m_150m";
  return "150m_plus";
}

/**
 * @param {string|null|undefined} fieldName
 * @returns {string|null}
 */
export function getValidationErrorCategory(fieldName) {
  const step = getFormStepForField(fieldName);
  return step ? `${step}_validation` : "validation_error";
}

/**
 * @param {string|null|undefined} fieldName
 * @returns {string|null}
 */
export function getFormStepForField(fieldName) {
  if (!fieldName || fieldName === "honeypot") return null;
  return FIELD_FORM_STEPS[fieldName] || null;
}

/**
 * @param {number} startMs
 * @returns {string|null}
 */
export function getElapsedTimeBucket(startMs) {
  if (!Number.isFinite(startMs) || startMs <= 0) return null;
  const seconds = Math.floor((Date.now() - startMs) / 1000);
  if (seconds < 30) return "under_30s";
  if (seconds < 120) return "30s_2m";
  if (seconds < 300) return "2m_5m";
  return "over_5m";
}

/**
 * @param {Iterable<string>} changedFields
 * @returns {string|null}
 */
export function resolveLastCompletedFormStep(changedFields) {
  let lastIndex = -1;
  for (const fieldName of changedFields) {
    const step = getFormStepForField(fieldName);
    if (!step) continue;
    const index = FORM_STEP_ORDER.indexOf(step);
    if (index > lastIndex) lastIndex = index;
  }
  return lastIndex >= 0 ? FORM_STEP_ORDER[lastIndex] : null;
}

/**
 * @param {object} payload
 * @returns {object}
 */
export function sanitizeSellerIntakeAnalyticsPayload(payload = {}) {
  if (!payload || typeof payload !== "object") return {};

  const clean = {};
  for (const [key, value] of Object.entries(payload)) {
    if (!ALLOWED_KEY_SET.has(key) || containsBlockedKey(key)) continue;
    if (value == null || value === "") continue;

    if (key === "errorFields" && Array.isArray(value)) {
      const fields = value.filter((entry) => typeof entry === "string" && !containsBlockedKey(entry));
      if (fields.length) clean.errorFields = fields;
      continue;
    }

    if (typeof value === "object" && !Array.isArray(value)) continue;
    clean[key] = value;
  }

  return clean;
}

/**
 * @param {{ sourceComponent?: string, sourceAction?: string }} [attributionOverrides]
 * @returns {object}
 */
export function buildSellerIntakeBaseAnalyticsPayload(attributionOverrides = {}) {
  const attribution = buildSellerSubmissionAttribution({
    sourceComponent: attributionOverrides.sourceComponent || SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_FORM,
    sourceAction: attributionOverrides.sourceAction || null,
    currentPath: SELL_YOUR_CAR_PATH,
  });

  return sanitizeSellerIntakeAnalyticsPayload({
    analyticsDomain: SELLER_INTAKE_ANALYTICS_DOMAIN,
    eventSource: "client",
    sourcePage: attribution.sourcePage || SELLER_SOURCE_PAGES.SELL_YOUR_CAR,
    sourceComponent: attribution.sourceComponent,
    sourceAction: attribution.sourceAction,
    route: SELL_YOUR_CAR_PATH,
    currentPath: attribution.currentPath,
    referrer: attribution.referrer,
    utmSource: attribution.utmSource,
    utmMedium: attribution.utmMedium,
    utmCampaign: attribution.utmCampaign,
    utmContent: attribution.utmContent,
    utmTerm: attribution.utmTerm,
    viewportCategory: getSellerIntakeViewportCategory(),
    deviceCategory: getSellerIntakeViewportCategory(),
  });
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionInput} input
 * @param {object} [extra]
 * @returns {object}
 */
export function buildSellerIntakeSubmitAttemptPayload(input = {}, extra = {}) {
  return sanitizeSellerIntakeAnalyticsPayload({
    ...buildSellerIntakeBaseAnalyticsPayload(),
    sellerType: input.sellerType || null,
    preferredContactMethod: input.preferredContactMethod || null,
    ownershipStatus: input.ownershipStatus || null,
    vehicleYearBucket: getVehicleYearBucket(input.vehicleYear),
    mileageBucket: getMileageBucket(input.vehicleMileageKm),
    askingPriceBucket: getAskingPriceBucket(input.askingPrice, input.currency),
    cityProvided: Boolean(pickString(input.city)),
    departmentProvided: Boolean(pickString(input.department)),
    emailProvided: Boolean(pickString(input.sellerEmail)),
    notesProvided: Boolean(pickString(input.notes)),
    hasPhotos: Boolean(input.hasPhotos),
    citySlug: normalizeSellerCityForAnalytics(input.city),
    departmentSlug: normalizeSellerCityForAnalytics(input.department),
    ...extra,
  });
}

/**
 * @param {Record<string, string>} fieldErrors
 * @param {object} [extra]
 * @returns {object}
 */
export function buildSellerIntakeValidationErrorPayload(fieldErrors = {}, extra = {}) {
  const errorFields = Object.keys(fieldErrors).filter((key) => key !== "form" && key !== "honeypot");
  const primaryErrorField = errorFields[0] || null;

  return sanitizeSellerIntakeAnalyticsPayload({
    ...buildSellerIntakeBaseAnalyticsPayload(),
    validationErrorCount: errorFields.length,
    errorFields,
    primaryErrorField,
    errorCategory: primaryErrorField ? getValidationErrorCategory(primaryErrorField) : "validation_error",
    formStep: primaryErrorField ? getFormStepForField(primaryErrorField) : null,
    ...extra,
  });
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionResult|null|undefined} result
 * @param {object} [extra]
 * @returns {object}
 */
export function buildSellerIntakeResultPayload(result, extra = {}) {
  const payload = {
    ...buildSellerIntakeBaseAnalyticsPayload(),
    resultStatus: result?.status || null,
    retryable: Boolean(result?.retryable),
    ...extra,
  };

  if (result && !result.ok) {
    payload.errorCategory = mapSellerResultToErrorCategory(result);
    const fieldErrors = result.formErrors ? Object.keys(result.formErrors).filter((k) => k !== "form") : [];
    if (fieldErrors.length) {
      payload.validationErrorCount = fieldErrors.length;
      payload.errorFields = fieldErrors;
      payload.primaryErrorField = fieldErrors[0];
    }
  }

  return sanitizeSellerIntakeAnalyticsPayload(payload);
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionResult|null|undefined} result
 * @returns {string|null}
 */
export function mapSellerResultToErrorCategory(result) {
  if (!result || result.ok) return null;
  const status = result.status;
  if (status === SELLER_SUBMISSION_STATUSES.DUPLICATE) return "duplicate";
  if (status === SELLER_SUBMISSION_STATUSES.RATE_LIMITED) return "rate_limited";
  if (status === SELLER_SUBMISSION_STATUSES.SPAM_REJECTED) return "spam_rejected";
  if (status === SELLER_SUBMISSION_STATUSES.VALIDATION_ERROR) return "validation_error";
  if (status === SELLER_SUBMISSION_STATUSES.NETWORK_ERROR) return "network_error";
  if (status === SELLER_SUBMISSION_STATUSES.SERVICE_ERROR) return "service_error";
  return "unknown_error";
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionResult|null|undefined} result
 * @returns {string}
 */
export function mapSellerSubmissionResultToEventName(result) {
  if (!result) return SELLER_INTAKE_ANALYTICS_EVENTS.SUBMIT_FAILED;
  if (result.ok) return SELLER_INTAKE_ANALYTICS_EVENTS.SUBMIT_SUCCEEDED;
  return SELLER_SUBMISSION_STATUS_EVENT_MAP[result.status] || SELLER_INTAKE_ANALYTICS_EVENTS.SUBMIT_FAILED;
}

/**
 * @param {string} eventName
 * @param {object} [payload]
 * @returns {object|null}
 */
export function trackSellerIntakeAnalytics(eventName, payload = {}) {
  if (!eventName) return null;
  try {
    if (!isSellerIntakeAnalyticsEnabled()) return null;
    const safePayload = sanitizeSellerIntakeAnalyticsPayload({
      ...buildSellerIntakeBaseAnalyticsPayload(),
      ...payload,
    });
    return trackEvent(eventName, safePayload);
  } catch {
    return null;
  }
}

/**
 * @param {{ sourceComponent?: string, sourceAction?: string }} [attribution]
 */
export function trackSellerIntakePageView(attribution = {}) {
  return trackSellerIntakeAnalytics(SELLER_INTAKE_ANALYTICS_EVENTS.PAGE_VIEWED, {
    ...buildSellerIntakeBaseAnalyticsPayload(attribution),
    sourceComponent: attribution.sourceComponent || SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_FORM,
  });
}

/**
 * @param {'primary'|'secondary'} ctaType
 * @param {{ sourceComponent?: string, sourceAction?: string }} [attribution]
 */
export function trackSellerIntakeCtaClick(ctaType, attribution = {}) {
  const eventName =
    ctaType === "secondary"
      ? SELLER_INTAKE_ANALYTICS_EVENTS.SECONDARY_CTA_CLICKED
      : SELLER_INTAKE_ANALYTICS_EVENTS.PRIMARY_CTA_CLICKED;

  return trackSellerIntakeAnalytics(eventName, buildSellerIntakeBaseAnalyticsPayload(attribution));
}

/**
 * @param {object} [extra]
 */
export function trackSellerIntakeFormStarted(extra = {}) {
  return trackSellerIntakeAnalytics(SELLER_INTAKE_ANALYTICS_EVENTS.FORM_STARTED, {
    sourceComponent: SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_FORM,
    ...extra,
  });
}

/**
 * @param {string} fieldName
 * @param {object} [extra]
 */
export function trackSellerIntakeFieldChanged(fieldName, extra = {}) {
  if (!fieldName || fieldName === "honeypot") return null;
  return trackSellerIntakeAnalytics(SELLER_INTAKE_ANALYTICS_EVENTS.FIELD_CHANGED, {
    fieldName,
    formStep: getFormStepForField(fieldName),
    ...extra,
  });
}

/**
 * @param {Record<string, string>} fieldErrors
 * @param {object} [extra]
 */
export function trackSellerIntakeValidationError(fieldErrors, extra = {}) {
  return trackSellerIntakeAnalytics(
    SELLER_INTAKE_ANALYTICS_EVENTS.VALIDATION_ERROR,
    buildSellerIntakeValidationErrorPayload(fieldErrors, extra)
  );
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionInput} input
 * @param {object} [extra]
 */
export function trackSellerIntakeSubmitAttempted(input, extra = {}) {
  return trackSellerIntakeAnalytics(
    SELLER_INTAKE_ANALYTICS_EVENTS.SUBMIT_ATTEMPTED,
    buildSellerIntakeSubmitAttemptPayload(input, extra)
  );
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionResult|null|undefined} result
 * @param {object} [extra]
 */
export function trackSellerIntakeSubmissionResult(result, extra = {}) {
  const eventName = mapSellerSubmissionResultToEventName(result);
  return trackSellerIntakeAnalytics(eventName, buildSellerIntakeResultPayload(result, extra));
}

/**
 * @param {object} [extra]
 */
export function trackSellerIntakeResetClicked(extra = {}) {
  return trackSellerIntakeAnalytics(SELLER_INTAKE_ANALYTICS_EVENTS.RESET_CLICKED, {
    sourceComponent: SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_FORM,
    sourceAction: "submit_another_vehicle",
    ...extra,
  });
}

/**
 * @param {object} [extra]
 */
export function trackSellerIntakeFormAbandoned(extra = {}) {
  return trackSellerIntakeAnalytics(SELLER_INTAKE_ANALYTICS_EVENTS.FORM_ABANDONED, {
    started: true,
    submitted: false,
    route: SELL_YOUR_CAR_PATH,
    ...extra,
  });
}
